import { createFileRoute, stripSearchParams } from '@tanstack/react-router';
import { useEffect } from 'react';

import { useBackground } from '~/components/Providers/BackgroundProvider';
import { getFullLevelThumbnail, seo } from '~/util';
import { clanMembersQuery } from '~/util/queries/public/clans';
import ClanProfileSummary from './-ClanProfileSummary';
import ClanProfileMain from './-ClanProfileMain';
import { clanParamsSchema, defaultClanSearchParams } from './-SearchParams';
import { clanProfileQuery } from '~/util/queries/public/profiles';
import { DefaultNotFound } from '~/components/DefaultNotFound';

export const Route = createFileRoute('/profile/clan/$clanid')({
    validateSearch: clanParamsSchema,
    search: {
        middlewares: [stripSearchParams(defaultClanSearchParams)],
    },
    loaderDeps: ({ search: { list } }) => ({ list }),
    loader: async ({
        context: { queryClient },
        params: { clanid },
        deps: { list },
    }) => {
        const [classic, platformer, members] = await Promise.all([
            queryClient.fetchQuery(
                clanProfileQuery({ list: 'classic', clanid }),
            ),
            queryClient.fetchQuery(
                clanProfileQuery({ list: 'platformer', clanid }),
            ),
            queryClient.fetchQuery(clanMembersQuery({ clanid })),
        ]);

        const classicTotal = (
            classic?.records?.filter(
                (record) =>
                    record.level.status == 'MainList' ||
                    record.level.status == 'Pending',
            ) ?? []
        ).length;

        const platformerTotal = (
            platformer?.records?.filter(
                (record) =>
                    record.level.status == 'MainList' ||
                    record.level.status == 'Pending',
            ) ?? []
        ).length;

        return {
            profile: list === 'classic' ? classic : platformer,
            total: list === 'classic' ? classicTotal : platformerTotal,
            classicTotal,
            platformerTotal,
            members,
        };
    },
    head: ({ loaderData }) => {
        const { profile } = loaderData || {};

        return {
            meta: seo({
                title: `[${profile?.clan.tag || ''}] ${profile?.clan.global_name || 'Unknown Clan'} Clan's Profile`,
                description:
                    profile?.clan?.description ||
                    "This clan hasn't provided a description yet.",
            }),
        };
    },
    component: ClanProfile,
    notFoundComponent: () =>
        DefaultNotFound({ customNotFound: 'Clan not found.' }),
});

function ClanProfile() {
    const { profile } = Route.useLoaderData({
        select: ({ profile }) => ({ profile }),
    });
    const { setBackground, setOpacity, resetBackground } = useBackground();

    useEffect(() => {
        if (profile?.records && profile.records[0]?.level?.level_id) {
            resetBackground();
            setBackground(
                getFullLevelThumbnail(profile.records[0].level.level_id),
            );
            setOpacity('0.05');
        }
    }, [profile, setBackground, setOpacity, resetBackground]);

    useEffect(() => {
        return resetBackground;
    }, [resetBackground]);

    return (
        <main className="h-full flex justify-center">
            <div className="flex flex-col items-center gap-4 w-full h-full bg-background-dark rounded-2xl py-4">
                <div className="flex flex-col lg:flex-row w-full  justify-center items-center lg:items-start lg:justify-start">
                    <ClanProfileSummary />
                    <ClanProfileMain />
                </div>
            </div>
        </main>
    );
}
